import { useEffect, useRef } from "react";
import { BrowserQRCodeReader } from "@zxing/browser";
import { DecodeHintType } from "@zxing/library";
import {
  drawCenterCrop,
  enhanceFrameForQr,
  sharpenFrameForQr,
} from "../utils/qr-scan-preprocess";

const WASM_OPTS = {
  formats: ["QRCode"],
  tryHarder: true,
  tryInvert: true,
  tryRotate: true,
  tryDownscale: false,
  tryDenoise: true,
};

const WASM_OPTS_ALT = {
  ...WASM_OPTS,
  binarizer: "GlobalHistogram",
};

const LOOP_MS = 250;
const MAX_UPSCALE = 1;
const START_DELAY_MS = 2500;

const FRAME_VARIANTS = [
  {
    crop: 0.35,
    enhance: true,
    sharpen: true,
    contrast: 55,
    targetPx: 1600,
    wasmOpts: WASM_OPTS,
  },
  {
    crop: 0.6,
    enhance: true,
    sharpen: false,
    contrast: 45,
    targetPx: 1600,
    wasmOpts: WASM_OPTS_ALT,
  },
  {
    crop: 1,
    enhance: false,
    sharpen: false,
    targetPx: 1600,
    wasmOpts: WASM_OPTS,
  },
];

const findVideo = (container) => container?.querySelector("video") ?? null;

const drawFullFrame = (video, canvas, ctx, targetPx) => {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return false;

  const scale = Math.min(targetPx / vw, targetPx / vh, MAX_UPSCALE);
  canvas.width = Math.max(1, Math.floor(vw * scale));
  canvas.height = Math.max(1, Math.floor(vh * scale));
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return true;
};

const createBrowserReader = () => {
  const reader = new BrowserQRCodeReader();
  reader.hints.set(DecodeHintType.TRY_HARDER, true);
  return reader;
};

const decodeFrame = async (canvas, ctx, wasmOpts, browserReader) => {
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  try {
    const { readBarcodes } = await import("zxing-wasm/full");
    const wasmResults = await readBarcodes(imageData, wasmOpts);
    if (wasmResults[0]?.text) return wasmResults[0].text;
  } catch {
    // Fall through to browser decoder.
  }

  try {
    const result = await browserReader.decodeFromCanvas(canvas);
    return result?.getText() ?? null;
  } catch {
    return null;
  }
};

const useDesktopPrintedQrBoost = ({ containerRef, enabled, onScan }) => {
  const onScanRef = useRef(onScan);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    if (!enabled) return undefined;

    let stopped = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const browserReader = createBrowserReader();
    let variantIndex = 0;
    let timeoutId = null;
    let decoding = false;

    const schedule = (delay = LOOP_MS) => {
      timeoutId = window.setTimeout(runLoop, delay);
    };

    const runLoop = async () => {
      if (stopped) return;

      if (decoding) {
        schedule();
        return;
      }

      const video = findVideo(containerRef.current);
      if (!video || video.readyState < 2 || !video.videoWidth) {
        schedule(100);
        return;
      }

      decoding = true;
      try {
        const variant = FRAME_VARIANTS[variantIndex % FRAME_VARIANTS.length];
        variantIndex += 1;

        const drew =
          variant.crop >= 1
            ? drawFullFrame(video, canvas, ctx, variant.targetPx)
            : drawCenterCrop(
                video,
                canvas,
                ctx,
                variant.crop,
                variant.targetPx,
                MAX_UPSCALE,
              );

        if (!drew) {
          decoding = false;
          schedule();
          return;
        }

        if (variant.enhance) {
          enhanceFrameForQr(ctx, canvas.width, canvas.height, {
            contrast: variant.contrast,
          });
        }

        if (variant.sharpen) {
          sharpenFrameForQr(ctx, canvas.width, canvas.height);
        }

        const text = await decodeFrame(
          canvas,
          ctx,
          variant.wasmOpts,
          browserReader,
        );

        if (text && !stopped) {
          stopped = true;
          onScanRef.current([{ rawValue: text }]);
          return;
        }
      } catch {
      } finally {
        decoding = false;
      }

      if (!stopped) schedule();
    };

    schedule(START_DELAY_MS);

    return () => {
      stopped = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [enabled, containerRef]);
};

export default useDesktopPrintedQrBoost;
